<div class="dash-shell">
  <!-- Hero -->
  <header class="dash-hero">
    <div class="dash-hero__body">
      <p class="dash-hero__eyebrow">{{ agencyName() ? 'Agency' : 'Agent' }}</p>
      <h1 class="dash-hero__title">
        @if (agencyName(); as agency) {
          {{ agency }}
        } @else if (firstName(); as name) {
          {{ name }}'s portfolio
        } @else {
          Your portfolio
        }
      </h1>
      <p class="dash-hero__sub">{{ heroSub() }}</p>
    </div>
    <div class="dash-hero__actions">
      <span class="agent-role-badge">{{ isAgencyAdmin() ? 'Agency admin' : 'Agent' }}</span>
      <div class="agent-menu">
        <button
          type="button"
          class="agent-menu__trigger"
          [class.agent-menu__trigger--open]="menuOpen()"
          (click)="toggleMenu()"
          aria-haspopup="menu"
          [attr.aria-expanded]="menuOpen()"
          aria-label="Open actions menu"
        >
          <app-icon name="menu" [size]="20" />
        </button>
        @if (menuOpen()) {
          <!-- Click-catcher closes the menu on outside click -->
          <button
            type="button"
            class="agent-menu__backdrop"
            aria-label="Close menu"
            (click)="closeMenu()"
          ></button>
          <div class="agent-menu__panel" role="menu">
            @if (isAgencyAdmin()) {
              <button type="button" role="menuitem" (click)="goTo('/agent/team')">
                <app-icon name="users" [size]="18" />
                <span>Manage Team</span>
              </button>
            }
            <button type="button" role="menuitem" (click)="goTo('/agent/clients/new')">
              <app-icon name="plus" [size]="18" />
              <span>Add Client</span>
            </button>
          </div>
        }
      </div>
    </div>
  </header>

  @if (isLoading()) {
    <div class="dash-kpi-grid">
      @for (i of [1, 2, 3, 4]; track i) {
        <div class="dash-skeleton" style="min-height: 116px"></div>
      }
    </div>
    <div class="dash-skeleton" style="min-height: 320px"></div>
    <div class="agent-grid">
      @for (i of [1, 2, 3, 4]; track i) {
        <div class="dash-skeleton" style="min-height: 150px"></div>
      }
    </div>
  } @else if (loadError()) {
    <div class="dash-panel">
      <div class="dash-empty">
        <span class="dash-empty__icon"><app-icon name="alert-triangle" [size]="32" /></span>
        <p class="dash-empty__title">Couldn't load your portfolio</p>
        <p class="dash-empty__body">Something went wrong on our side.</p>
        <a class="agent-link" routerLink="/dashboard">Back to home</a>
      </div>
    </div>
  } @else if (!hasClients()) {
    <!-- Empty: no linked clients -->
    <div class="dash-panel">
      <div class="dash-empty">
        <span class="dash-empty__icon"><app-icon name="briefcase" [size]="40" /></span>
        <p class="dash-empty__title">Add your first client</p>
        <p class="dash-empty__body">
          Set up a business you represent and invite its owner. You can start preparing their
          Kickstart Cash or Debtors Finance application right away.
        </p>
        <a class="agent-cta agent-cta--solid" routerLink="/agent/clients/new">
          <app-icon name="plus" [size]="18" />
          <span>Add a client</span>
        </a>
      </div>
    </div>
  } @else {
    <!-- KPI row -->
    <section class="dash-kpi-grid" aria-label="Portfolio at a glance">
      @for (kpi of kpis(); track kpi.label; let i = $index) {
        <div class="dash-kpi dash-enter" [style.--dash-index]="i">
          <div class="dash-kpi__top">
            <span
              class="dash-kpi__icon"
              [class.dash-kpi__icon--success]="kpi.tone === 'success'"
              [class.dash-kpi__icon--warning]="kpi.tone === 'warning'"
            >
              <app-icon [name]="kpi.icon" [size]="18" />
            </span>
          </div>
          <div>
            <p class="dash-kpi__value" [class.dash-kpi__value--sm]="kpi.isMoney">
              @if (kpi.isMoney) {
                {{ kpi.raw | zar }}
              } @else {
                {{ kpi.value }}
              }
            </p>
            <p class="dash-kpi__label">{{ kpi.label }}</p>
          </div>
        </div>
      }
    </section>

    <!-- Portfolio status + needs attention -->
    <section class="dash-grid-2" aria-label="Portfolio insight">
      <div class="dash-panel">
        <div class="dash-panel__header">
          <div>
            <p class="dash-panel__title">Needs your attention</p>
            <p class="dash-panel__subtitle">
              Applications waiting on something from the business — follow up first
            </p>
          </div>
          <span class="dash-panel__icon"><app-icon name="alert-triangle" [size]="18" /></span>
        </div>

        @if (attention().length === 0) {
          <div class="dash-empty">
            <span class="dash-empty__icon dash-empty__icon--ok"
              ><app-icon name="check" [size]="28"
            /></span>
            <p class="dash-empty__title">Nothing needs attention</p>
            <p class="dash-empty__body">All your clients' applications are moving.</p>
          </div>
        } @else {
          <ul class="agent-attention-list">
            @for (item of attention(); track item.short_id) {
              <li>
                <a class="agent-attention" [routerLink]="appLink(item)">
                  <span class="agent-attention__product" [attr.data-product]="item.product">{{
                    item.product
                  }}</span>
                  <span class="agent-attention__main">
                    @if (item.first_item) {
                      <!-- DOC-73 UI-DA-73-05: the first item the business owes, in
                           the back end's words. -->
                      <span class="agent-attention__entity" data-testid="attention-first-item"
                        >{{ item.entity_name }} — Kickstart Cash: {{ item.first_item }}</span
                      >
                    } @else {
                      <span class="agent-attention__entity">{{ item.entity_name }}</span>
                    }
                    @if (item.amount) {
                      <span class="agent-attention__amount">Applied for {{ item.amount | zar }}</span>
                    }
                  </span>
                  <span class="agent-attention__stage" data-testid="stage-name">{{ item.stage_name }}</span>
                  <app-icon class="agent-attention__chevron" name="arrow-right" [size]="16" />
                </a>
              </li>
            }
          </ul>
        }
      </div>

      <div class="dash-panel">
        <div class="dash-panel__header">
          <div>
            <p class="dash-panel__title">Live applications</p>
            <p class="dash-panel__subtitle">Everything in flight across your clients</p>
          </div>
          <span class="dash-panel__icon"><app-icon name="chart" [size]="18" /></span>
        </div>

        @if (live().length === 0) {
          <div class="dash-empty">
            <span class="dash-empty__icon"><app-icon name="inbox" [size]="28" /></span>
            <p class="dash-empty__title">No live applications</p>
            <p class="dash-empty__body">
              Submitted applications show here while they're in review.
            </p>
          </div>
        } @else {
          <ul class="agent-attention-list">
            @for (item of live(); track item.short_id) {
              <li>
                <a class="agent-attention" [routerLink]="appLink(item)">
                  <span class="agent-attention__product" [attr.data-product]="item.product">{{
                    item.product
                  }}</span>
                  <span class="agent-attention__main">
                    <span class="agent-attention__entity">{{ item.entity_name }}</span>
                    @if (item.amount) {
                      <span class="agent-attention__amount">Applied for {{ item.amount | zar }}</span>
                    }
                    @if (item.progress) {
                      <span class="agent-attention__amount" data-testid="live-progress">{{
                        item.progress
                      }}</span>
                    }
                  </span>
                  <span class="agent-attention__stage" data-testid="stage-name">{{ item.stage_name }}</span>
                  <app-icon class="agent-attention__chevron" name="arrow-right" [size]="16" />
                </a>
              </li>
            }
          </ul>
        }
      </div>
    </section>

    <!-- Client grid -->
    <section aria-label="All clients">
      <div class="agent-section-head">
        <h2 class="agent-section-title">All clients</h2>
        <a class="agent-link" routerLink="/agent/clients/new">+ Add a client</a>
      </div>
      <div class="agent-grid">
        @for (entity of entities(); track entity.short_id; let i = $index) {
          <article
            class="agent-card dash-enter"
            [style.--dash-index]="i"
            role="button"
            tabindex="0"
            [attr.aria-label]="'Open ' + entity.name"
            (click)="navigateTo(entity.short_id)"
            (keydown.enter)="navigateTo(entity.short_id)"
            (keydown.space)="navigateTo(entity.short_id); $event.preventDefault()"
          >
            <div class="agent-card__header">
              <span class="agent-card__avatar"><app-icon name="building" [size]="22" /></span>
              <div class="agent-card__heading">
                <h3 class="agent-card__name">{{ entity.name }}</h3>
                @if (entity.trading_name) {
                  <p class="agent-card__trading">Trading as {{ entity.trading_name }}</p>
                }
              </div>
            </div>
            <p class="agent-card__meta">{{ getTypeLabel(entity.type) }} · {{ entity.reg_no }}</p>
            @if (entity.status === 'pending_activation') {
              <p class="agent-card__status agent-card__status--pending">
                <span class="agent-card__dot"></span>Awaiting owner activation
              </p>
            } @else {
              <p
                class="agent-card__status"
                [class.agent-card__status--attention]="
                  clientStatus(entity.short_id).tone === 'attention'
                "
                [class.agent-card__status--positive]="
                  clientStatus(entity.short_id).tone === 'positive'
                "
              >
                <span class="agent-card__dot"></span>{{ clientStatus(entity.short_id).label }}
              </p>
            }
            <div class="agent-card__footer">
              <span class="agent-card__open">Open</span>
              <app-icon class="agent-card__chevron" name="arrow-right" [size]="18" />
            </div>
          </article>
        }

        <!-- Add-a-client tile -->
        <a class="agent-add-card" routerLink="/agent/clients/new">
          <span class="agent-add-card__icon"><app-icon name="plus" [size]="22" /></span>
          <span class="agent-add-card__label">Add a client</span>
          <span class="agent-add-card__hint">Onboard a business you represent</span>
        </a>
      </div>
    </section>
  }
</div>
